// src/utils/storageManager.js
class StorageManager {
    constructor() {
      this.dbName = 'CelticDruidMemories';
      this.dbVersion = 1;
      this.storeName = 'memories';
    }
  
    // Inizializza il database
    async initDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.dbVersion);
        
        request.onerror = () => reject(request.error);
        
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
            store.createIndex('createdAt', 'createdAt', { unique: false });
          }
        };
      });
    }
  
    // Salva un ricordo
    async saveMemory(memory) {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        
        return new Promise((resolve, reject) => {
          const request = store.put(memory);
          request.onsuccess = () => resolve(memory);
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error('Errore nel salvare il ricordo:', error);
        throw error;
      }
    }
  
    // Carica tutti i ricordi
    async loadMemories() {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        
        return new Promise((resolve, reject) => {
          const request = store.getAll();
          request.onsuccess = () => {
            // Ordina per data di creazione (più recenti prima)
            const memories = request.result.sort((a, b) => 
              new Date(b.createdAt) - new Date(a.createdAt)
            );
            resolve(memories);
          };
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error('Errore nel caricare i ricordi:', error);
        return [];
      }
    }
  
    // Elimina un ricordo
    async deleteMemory(memoryId) {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        
        return new Promise((resolve, reject) => {
          const request = store.delete(memoryId);
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error('Errore nell\'eliminare il ricordo:', error);
        throw error;
      }
    }
  
    // Pulisci tutti i ricordi (per debug/reset)
    async clearAllMemories() {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        
        return new Promise((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        console.error('Errore nel pulire i ricordi:', error);
        throw error;
      }
    }
  
    // Ottieni statistiche di utilizzo
    async getStorageStats() {
      try {
        const memories = await this.loadMemories();
        const totalMemories = memories.length;
        
        // Calcola dimensione approssimativa
        let totalSize = 0;
        memories.forEach(memory => {
          if (memory.compositeImage) {
            // Stima dimensione base64 image
            totalSize += memory.compositeImage.length * 0.75; // base64 è ~75% della dimensione originale
          }
          if (memory.userPhoto) {
            totalSize += memory.userPhoto.length * 0.75;
          }
        });
  
        return {
          totalMemories,
          totalSizeBytes: Math.round(totalSize),
          totalSizeMB: Math.round(totalSize / (1024 * 1024) * 100) / 100,
          oldestMemory: memories.length > 0 ? memories[memories.length - 1].createdAt : null,
          newestMemory: memories.length > 0 ? memories[0].createdAt : null
        };
      } catch (error) {
        console.error('Errore nel calcolare le statistiche:', error);
        return null;
      }
    }
  }
  
  // Esporta un'istanza singleton
  export const storageManager = new StorageManager();
  export default StorageManager;